import camelcaseKeys from 'camelcase-keys';
import ActivityAPI from 'dashboard/api/activities';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import snakecaseKeys from 'snakecase-keys';

const camelizeActivity = data => camelcaseKeys(data || {}, { deep: true });

const normalizeMeta = meta => ({
  ...camelcaseKeys(meta || {}),
  totalCount: Number(meta?.total_count || meta?.totalCount || meta?.count || 0),
  page: Number(meta?.page || meta?.current_page || 1),
});

const buildActivityPayload = attrs => ({
  activity: snakecaseKeys(attrs, { deep: true }),
});

export const useActivitiesStore = createStore({
  name: 'activities',
  type: 'pinia',
  API: ActivityAPI,
  state: () => ({
    activeActivityId: null,
  }),

  getters: {
    getActivitiesList: state => state.records,
    getActiveActivity: state =>
      state.records.find(a => a.id === state.activeActivityId) || {},
    getPendingActivities: state =>
      state.records.filter(a => a.status === 'pending'),
    getOverdueActivities: state =>
      state.records.filter(
        a =>
          a.status === 'pending' &&
          a.dueDate &&
          new Date(a.dueDate * 1000) < new Date()
      ),
  },

  actions: () => ({
    setMeta(meta) {
      this.meta = normalizeMeta(meta);
    },

    setActiveActivityId(activityId) {
      this.activeActivityId = Number(activityId);
    },

    upsertActivityRecord(record) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = record;
    },

    async get(params = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const response = await ActivityAPI.get(params);
        const activities = (response.data.payload || []).map(a =>
          camelizeActivity(a)
        );
        this.records = activities;
        if (response.data.meta) this.setMeta(response.data.meta);
        return activities;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      this.setActiveActivityId(id);
      try {
        const response = await ActivityAPI.show(id);
        const activity = camelizeActivity(response.data.payload);
        this.upsertActivityRecord(activity);
        return activity;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingItem: false });
      }
    },

    async create(activityAttrs) {
      this.setUIFlag({ creatingItem: true });
      try {
        const response = await ActivityAPI.create(
          buildActivityPayload(activityAttrs)
        );
        const activity = camelizeActivity(response.data.payload);
        this.upsertActivityRecord(activity);
        return activity;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update({ id, ...activityAttrs }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const response = await ActivityAPI.update(
          id,
          buildActivityPayload(activityAttrs)
        );
        const activity = camelizeActivity(response.data.payload);
        this.upsertActivityRecord(activity);
        return activity;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        await ActivityAPI.delete(id);
        this.records = this.records.filter(r => r.id !== Number(id));
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    async complete(activityId) {
      this.setUIFlag({ completing: true });
      try {
        const response = await ActivityAPI.complete(activityId);
        const activity = camelizeActivity(response.data.payload);
        this.upsertActivityRecord(activity);
        return activity;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ completing: false });
      }
    },
  }),
});
